import { useCallback, useEffect, useRef, useState } from 'react';

export const DEFAULT_REVEAL_CHARS_PER_SECOND = 200;

export type TRevealPlan = { totalChars: number; reveal: (charsToShow: number) => void };

// Drives a requestAnimationFrame loop that calls `reveal(n)` with a growing character count until the
// whole source is shown. The HTML and text reveal hooks differ only in how they build the plan
// (`createPlan`); the timing, skip-to-end and isAnimating machinery lives here once.
export const useRevealEngine = (
  source: string,
  createPlan: (source: string) => TRevealPlan | null,
  charsPerSecond: number
) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const finalizeRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    const plan = createPlan(source);
    if (!plan) {
      setIsAnimating(false);
      return undefined;
    }
    const { totalChars, reveal } = plan;

    let frame: number | null = null;
    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      setIsAnimating(false);
    };

    finalizeRef.current = () => {
      reveal(totalChars);
      stop();
    };

    reveal(0);
    setIsAnimating(true);

    const startTime = performance.now();
    const tick = (now: number) => {
      const target = Math.floor(((now - startTime) / 1000) * charsPerSecond);
      reveal(target);
      if (target >= totalChars) {
        stop();
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return stop;
  }, [source, createPlan, charsPerSecond]);

  const complete = useCallback(() => finalizeRef.current(), []);
  return { isAnimating, complete };
};
